// StepOne.tsx
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useFormatting } from 'locale';
import * as yup from 'yup';

import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';

import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { notification } from 'ui-library';
import { getClientId } from 'utils';

import { CaseOrderStatus } from 'components/DocBox/DocBox.types';
import { useCreateCaseState } from './CreateCaseManager';
import { adaptCreateCasePayload, adaptSummaryUIData } from './adapter';
import { BankInformationEnum } from './CaseManagement.types';

const schema = yup.object({
    isEmailRetention: yup.boolean(),
});

type FormData = yup.InferType<typeof schema>;
type CaseDataResponse = { id: string };

const useSummary = () => {
    const { t, i18n: { language } } = useTranslation();
    const {
        typeOfOrderData,
        reasonForSaleData,
        bankInformationData,
        isPOA,
        beneficiaryInfoStatic,
        currentPortfolioSelector,
        investmentApplicationId,
        reasonForSaleOptions,
        saveCaseManagementData,
        isJA, isPA, isPK,
        containerId,
        isPaymentOrderPrivateAccount,
        active3bPortfolioOptions,
        isActive3bPortfoliosLoading,
    } = useCreateCaseState();

    const navigate = useNavigate();
    const { portfolioId, dfsClientId } = useParams();
    const { search } = useLocation();
    const contactId = useMemo(() => getClientId(dfsClientId), [dfsClientId]);

    const { getFormattedCurrency } = useFormatting();

    const {
        register, control, handleSubmit, reset, watch, formState: { errors },
    } = useForm<FormData>({
        resolver: useYupValidationResolver(schema),
    });

    const formData = watch();

    const {
        mutateAsync: handleMutation,
        isPending: isNewCaseOrderCreationInProgress,
    } = useGenericAPIMutation();

    const [loading, setLoading] = useState(isNewCaseOrderCreationInProgress);

    const onSubmit = useCallback(async (data: FormData) => {
        setLoading(true);
        const payload = adaptCreateCasePayload({
            payload: {
                contactId,
                portfolioId,
                investmentApplicationId,
                investedAmount: currentPortfolioSelector?.CurrentValue,
                typeOfOrderData,
                reasonForSaleData,
                bankInformationData,
                isPOA,
                isPK,
                isPaymentOrderPrivateAccount,
                isEmailRetention: data?.isEmailRetention,
            },
        });

        try {
            const { data: caseData } = await handleMutation({
                service: 'customCaseManagementServices',
                API: 'createNewCaseOrder',
                payload: { language, ...payload },
            }) as { data: CaseDataResponse };

            if (caseData?.id) {
                const response:any = await handleMutation({
                    service: 'customCaseManagementServices',
                    API: 'generateCaseDocument',
                    payload: {
                        caseId: caseData?.id,
                        language,
                    },
                    invalidateQueryKeys: ['getData_getCasesByPortfolioId'],
                });

                if (response?.data?.status === CaseOrderStatus.ReadyForSigning) {
                    navigate(`/client/${dfsClientId}/case/${caseData?.id}${containerId ? `?containerId=${containerId}` : ''}`);
                }
            }
        } catch (error) {
            notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [
        contactId,
        currentPortfolioSelector?.InvestedAmount,
        portfolioId,
        investmentApplicationId,
        typeOfOrderData,
        reasonForSaleData,
        bankInformationData,
        isPOA,
        isPaymentOrderPrivateAccount,
        adaptCreateCasePayload,
        handleMutation,
        language,
        containerId,
    ]);

    const handleEditCase = useCallback(() => {
        navigate(`/client/${dfsClientId}/portfolios/${portfolioId}/new-case/type-of-order${search}`);
    }, [dfsClientId, portfolioId, search]);

    const summaryUIData = adaptSummaryUIData({
        currentPortfolioSelector,
        typeOfOrderData,
        reasonForSaleData,
        bankInformationData,
        reasonForSaleOptions,
        t,
        getFormattedCurrency,
        active3bPortfolioOptions,
    });

    useEffect(() => {
        if (bankInformationData?.bankAccountOption === BankInformationEnum.Value5701 || (isJA && !isPA && bankInformationData?.bankAccountOption === BankInformationEnum.Value5701)) {
            saveCaseManagementData({
                bankInformationData: { ...bankInformationData, ...beneficiaryInfoStatic },
            });
        }
    }, [language]);

    useLayoutController({
        callbackOnContinue: handleSubmit(onSubmit),
        isContinueDisabled: loading,
        isContinueLoading: loading,
        reSubscriptionStates: [currentPortfolioSelector?.InvestedAmount, language, bankInformationData],
    });

    return {
        register,
        control,
        formData,
        setData: reset,
        errors,
        data: summaryUIData,
        t,
        handleEditCase,
        isPaymentOrderPrivateAccount,
        isActive3bPortfoliosLoading,
    };
};

export default useSummary;
